import { z } from "zod";
import {
  ImportanceSchema,
  ProviderKeySchema,
  SenderSchema,
  RecipientSchema,
} from "./common";

// ============================================================================
// CORE MESSAGE SCHEMAS
// ============================================================================

export const MessageBaseSchema = z.object({
  id: z.string(),
  subject: z.string(),
  body: z.string(),
  bodyPreview: z.string(),
  sender: SenderSchema,
  // The RFC 5322 Reply-To address(es), when the sender set them. For
  // mailing-list / "via" mail (e.g. "TechSoup via hopewellwatersupport@…")
  // the From is the list address, so replies MUST target Reply-To, not sender.
  replyTo: z.array(RecipientSchema).optional(),
  recipients: z.array(RecipientSchema),
  ccRecipients: z.array(RecipientSchema).optional(),
  bccRecipients: z.array(RecipientSchema).optional(),
  receivedDateTime: z.string().datetime(),
  sentDateTime: z.string().datetime(),
  isRead: z.boolean(),
  isArchived: z.boolean().optional(),
  isDeleted: z.boolean().optional(),
  hasAttachments: z.boolean(),
  importance: ImportanceSchema,
  conversationId: z.string(),
  webLink: z.string().url().optional(),
  provider: ProviderKeySchema,
  providerMessageId: z.string(),
  inboxId: z.string().uuid(),
}).strict();

export type MessageBase = z.infer<typeof MessageBaseSchema>;

/**
 * Canonical Message type.
 * Uses .passthrough() to maintain backward compatibility with index signatures.
 */
export const MessageSchema = MessageBaseSchema.passthrough();
export type Message = z.infer<typeof MessageSchema>;

/**
 * Message as the CRM entity layer consumes it.
 *
 * Deliberately the STRICT shape, not `Message`. `MessageSchema` is
 * `.passthrough()`, so `Message` carries an `[x: string]: unknown` index
 * signature — which made every field write through
 * `EntityHooks<MessageEntity>` accept any key at all. Parsing keeps the
 * passthrough (unknown keys still survive at runtime); the entity type is what
 * the CRM layer is allowed to read and write. See ENTITY_HOOKS_USE_CODEGEN_TYPES.
 */
export type MessageEntity = MessageBase;

// ============================================================================
// UNIFIED MESSAGE SCHEMAS
// ============================================================================

export const UnifiedMessageSchema = MessageBaseSchema.extend({
  // Provider-specific metadata
  metadata: z.record(z.string(), z.any()).optional(),

  // Computed properties
  isUnread: z.boolean(),
  isImportant: z.boolean(),
  hasReplies: z.boolean(),
  replyCount: z.number().int().nonnegative().optional(),
  threadId: z.string().optional(),
  
  // Optional associations
  channel: z.object({
    id: z.string(),
    name: z.string(),
  }).optional(),
  user: z.object({
    id: z.string(),
    name: z.string(),
    avatar: z.string().optional(),
  }).optional(),
}).strict();

export type UnifiedMessage = z.infer<typeof UnifiedMessageSchema>;

// ============================================================================
// RESPONSE SCHEMAS
// ============================================================================

export const MessageListResponseSchema = z.object({
  messages: z.array(MessageSchema),
  totalCount: z.number().int().nonnegative(),
  hasMore: z.boolean(),
  nextPageToken: z.string().optional(),
}).strict();

export type MessageListResponse = z.infer<typeof MessageListResponseSchema>;

export const UnifiedMessageListResponseSchema = z.object({
  messages: z.array(UnifiedMessageSchema),
  totalCount: z.number().int().nonnegative(),
  hasMore: z.boolean(),
  nextPageToken: z.string().optional(),
  filters: z.record(z.string(), z.any()),
  sort: z.object({
    field: z.string(),
    order: z.enum(["asc", "desc"]),
  }),
}).strict();

export type UnifiedMessageListResponse = z.infer<typeof UnifiedMessageListResponseSchema>;

// ============================================================================
// FILTER & SORT SCHEMAS
// ============================================================================

export const DateRangeSchema = z.object({
  start: z.string().datetime(),
  end: z.string().datetime(),
}).strict();

export const MessageFilterSchema = z.object({
  isRead: z.boolean().optional(),
  importance: ImportanceSchema.optional(),
  hasAttachments: z.boolean().optional(),
  providers: z.array(z.string()).optional(),
  inboxIds: z.array(z.string().uuid()).optional(),
  dateRange: DateRangeSchema.optional(),
  search: z.string().optional(),
  sender: z.string().optional(),
  subject: z.string().optional(),
  body: z.string().optional(),
  metadata: z.record(z.string(), z.any()).optional(),
}).strict();

export type MessageFilter = z.infer<typeof MessageFilterSchema>;

export const MessageSortFieldSchema = z.enum([
  "receivedDateTime",
  "sentDateTime",
  "subject",
  "sender",
  "importance",
  "provider",
]);
export type MessageSortField = z.infer<typeof MessageSortFieldSchema>;

export const MessageSortSchema = z.object({
  field: MessageSortFieldSchema,
  order: z.enum(["asc", "desc"]),
}).strict();

export type MessageSort = z.infer<typeof MessageSortSchema>;

// ============================================================================
// STATISTICS SCHEMAS
// ============================================================================

export const ProviderStatsSchema = z.object({
  total: z.number().int().nonnegative(),
  unread: z.number().int().nonnegative(),
  important: z.number().int().nonnegative(),
}).strict();

export const MessageStatsSchema = z.object({
  total: z.number().int().nonnegative(),
  unread: z.number().int().nonnegative(),
  important: z.number().int().nonnegative(),
  withAttachments: z.number().int().nonnegative(),
  byProvider: z.record(z.string(), ProviderStatsSchema),
  byInbox: z.record(z.string(), ProviderStatsSchema),
}).strict();

export type MessageStats = z.infer<typeof MessageStatsSchema>;

// ============================================================================
// TRANSFORMER & OPERATION TYPES
// ============================================================================

export interface MessageTransformer {
  transformMessage: (rawMessage: any, connectionId: string) => Promise<UnifiedMessage>;
  transformMessageList: (rawMessages: any[], connectionId: string) => Promise<UnifiedMessage[]>;
}

export interface MessageOperations {
  markAsRead: (messageId: string, provider: string, connectionId: string) => Promise<void>;
  markAsUnread: (messageId: string, provider: string, connectionId: string) => Promise<void>;
  reply: (
    messageId: string,
    provider: string,
    connectionId: string,
    replyText: string
  ) => Promise<void>;
  forward: (
    messageId: string,
    provider: string,
    connectionId: string,
    recipients: string[]
  ) => Promise<void>;
  delete: (messageId: string, provider: string, connectionId: string) => Promise<void>;
  archive: (messageId: string, provider: string, connectionId: string) => Promise<void>;
}
